/**
 * Shipping Options Module
 * Handles loading and displaying shipping options in checkout
 */

import { t } from '../core/i18n.js';
import { getCartType } from '../core/utils.js';
import { getStorage } from '../core/storage.js';

export class ShippingOptionsManager {
    constructor(system) {
        this.system = system;
        this.shippingOptions = [];
        this.selectedOption = null;
        this.merchantId = null;
        this.initialized = false;
    }

    init() {
        if (this.initialized) return;
        
        console.log('ShippingOptionsManager: Initializing...');
        
        // Check if shipping options section exists
        const shippingSection = document.querySelector('.shipping-options-section');
        if (!shippingSection) {
            console.log('ShippingOptionsManager: No shipping options section found');
            return;
        }

        // Check cart type - if only gift cards, hide shipping section entirely
        const cartItems = getStorage(this.system.config.storageKey) || [];
        const cartType = getCartType(cartItems);
        
        console.log('ShippingOptionsManager: Cart type detected:', cartType);
        
        if (cartType === 'only-giftcards') {
            console.log('ShippingOptionsManager: Only gift cards in cart - hiding shipping section');
            shippingSection.style.display = 'none';
            
            // Also hide shipping row in order summary if it exists
            const shippingRow = document.getElementById('order-shipping-row');
            if (shippingRow) {
                shippingRow.style.display = 'none';
            }
            
            return; // Exit early - no shipping functionality for gift cards only
        }

        this.merchantId = this.getMerchantId();
        if (!this.merchantId) {
            console.error('ShippingOptionsManager: No merchant ID found');
            this.showError(t('shipping.error'));
            return;
        }

        this.setupEventListeners();
        this.loadShippingOptions();
        this.initialized = true;
    }

    getMerchantId() {
        // Try to get merchant ID from various sources
        const sources = [
            () => this.system?.config?.checkout?.merchantId,
            () => window.ecommerce?.config?.checkout?.merchantId,
            () => document.querySelector('[data-merchant-id]')?.dataset.merchantId,
            () => window.merchantId
        ];

        for (const source of sources) {
            try {
                const id = source();
                if (id && id !== '00000000-0000-0000-0000-000000000001') {
                    return id;
                }
            } catch (e) {
                // Continue to next source
            }
        }

        // Fallback to default merchant ID for development
        return '00000000-0000-0000-0000-000000000001';
    }

    setupEventListeners() {
        // Listen for shipping option selection changes
        document.addEventListener('change', (e) => {
            if (e.target.name === 'shippingOption') {
                this.handleShippingOptionChange(e.target);
            }
        });

        // Retry button
        const retryButton = document.getElementById('retry-shipping');
        if (retryButton) {
            retryButton.addEventListener('click', () => {
                this.loadShippingOptions();
            });
        }

        // Listen for discount changes to update shipping display (with recursion protection)
        document.addEventListener('discountStackUpdated', () => {
            if (!this.isUpdatingFromDiscount) {
                console.log('ShippingOptionsManager: Discount stack updated, re-rendering shipping options');
                this.isUpdatingFromDiscount = true;
                this.renderShippingOptions();
                setTimeout(() => { this.isUpdatingFromDiscount = false; }, 100);
            }
        });

        document.addEventListener('automaticDiscountsApplied', () => {
            if (!this.isUpdatingFromDiscount) {
                console.log('ShippingOptionsManager: Automatic discounts applied, re-rendering shipping options');
                this.isUpdatingFromDiscount = true;
                this.renderShippingOptions();
                setTimeout(() => { this.isUpdatingFromDiscount = false; }, 100);
            }
        });

        document.addEventListener('manualDiscountApplied', () => {
            if (!this.isUpdatingFromDiscount) {
                console.log('ShippingOptionsManager: Manual discount applied, re-rendering shipping options');
                this.isUpdatingFromDiscount = true;
                this.renderShippingOptions();
                setTimeout(() => { this.isUpdatingFromDiscount = false; }, 100);
            }
        });

        document.addEventListener('discountCodeRemoved', () => {
            if (!this.isUpdatingFromDiscount) {
                console.log('ShippingOptionsManager: Discount removed, re-rendering shipping options');
                this.isUpdatingFromDiscount = true;
                this.renderShippingOptions();
                setTimeout(() => { this.isUpdatingFromDiscount = false; }, 100);
            }
        });
    }

    async loadShippingOptions() {
        console.log('ShippingOptionsManager: Loading shipping options for merchant:', this.merchantId);
        
        this.showLoading();

        try {
            const response = await fetch(`/cart/api/v1/ShippingOptions/merchant/${this.merchantId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('ShippingOptionsManager: Received shipping options:', data);

            // Handle shipping options response
            if (data.shippingOptions) {
                this.shippingOptions = data.shippingOptions;
            } else {
                // Fallback for old format
                this.shippingOptions = Array.isArray(data) ? data : (data.items || []);
            }
            
            if (this.shippingOptions.length === 0) {
                this.showNoOptions();
            } else {
                this.renderShippingOptions();
            }

        } catch (error) {
            console.error('ShippingOptionsManager: Failed to load shipping options:', error);
            this.showError(t('shipping.error'));
        }
    }

    renderShippingOptions() {
        const container = document.getElementById('shipping-options-list');
        if (!container) return;

        // Remember currently selected option to preserve user's choice
        const currentlySelected = document.querySelector('input[name="shippingOption"]:checked');
        const currentlySelectedId = currentlySelected ? currentlySelected.value : null;

        let html = '';
        
        this.shippingOptions.forEach((option, index) => {
            // Preserve user's selection, or default to first option if none selected
            const isChecked = currentlySelectedId ? 
                (option.id === currentlySelectedId ? 'checked' : '') : 
                (index === 0 ? 'checked' : '');
                
            const shippingMethodText = this.getShippingMethodText(option.shippingMethod);
            
            // Check if this shipping option should be free due to discounts
            const isFreeShipping = this.isShippingFree(option);
            const displayPrice = isFreeShipping ? 0 : option.price;
            
            html += `
                <div class="shipping-option">
                    <input type="radio" 
                           id="shipping-${option.id}" 
                           name="shippingOption" 
                           value="${option.id}"
                           data-price="${option.price}"
                           data-display-price="${displayPrice}"
                           data-name="${this.escapeHtml(option.name)}"
                           data-method="${option.shippingMethod}"
                           ${isChecked}
                           required>
                    <label for="shipping-${option.id}" class="shipping-option-label">
                        <div class="shipping-option-info">
                            <div class="shipping-option-name">${this.escapeHtml(option.name)}</div>
                            <div class="shipping-option-method">${shippingMethodText}</div>
                        </div>
                        <div class="shipping-option-price">
                            ${displayPrice > 0 ? `${displayPrice.toFixed(2)} kr` : t('shipping.free')}
                            ${isFreeShipping && option.price > 0 ? ` <span class="original-price" style="text-decoration: line-through; color: #999;">(${option.price.toFixed(2)} kr)</span>` : ''}
                        </div>
                    </label>
                </div>
            `;
        });

        container.innerHTML = html;
        this.hideError();

        // Update selectedOption based on what's actually checked after render
        const newlySelected = document.querySelector('input[name="shippingOption"]:checked');
        if (newlySelected) {
            const selectedOptionId = newlySelected.value;
            this.selectedOption = this.shippingOptions.find(opt => opt.id === selectedOptionId);
            
            // Only update totals if this is the initial render (no previous selection)
            if (!currentlySelectedId) {
                this.updateOrderTotals();
            }
        }
    }

    getShippingMethodText(shippingMethod) {
        // Use translation keys for shipping methods
        return t(`shipping.methods.${shippingMethod}`) || shippingMethod;
    }

    handleShippingOptionChange(radioButton) {
        const optionId = radioButton.value;
        const selectedOption = this.shippingOptions.find(opt => opt.id === optionId);
        
        if (selectedOption) {
            console.log('ShippingOptionsManager: Selected shipping option:', selectedOption);
            this.selectedOption = selectedOption;
            
            // Update shipping display first
            this.updateShippingDisplay();
            
            // Dispatch custom event for other modules
            const event = new CustomEvent('shippingOptionChanged', {
                detail: {
                    option: selectedOption,
                    price: selectedOption.price
                }
            });
            document.dispatchEvent(event);
            
            // Trigga event för automatiska rabatter (med delay för att undvika loops)
            setTimeout(() => {
                const shippingUpdatedEvent = new CustomEvent('shippingUpdated', {
                    detail: {
                        selectedOption: selectedOption,
                        shippingCost: this.getShippingCost()
                    }
                });
                document.dispatchEvent(shippingUpdatedEvent);
            }, 50);
        }
    }

    getCurrentOrderTotal() {
        // Get current subtotal from the order summary
        const subtotalAmount = document.getElementById('order-subtotal-amount');
        if (subtotalAmount) {
            return this.parsePrice(subtotalAmount.textContent);
        }
        
        // Fallback: try to get from cart system
        if (this.system && this.system.cart) {
            return this.system.cart.getTotal ? this.system.cart.getTotal() : 0;
        }
        
        // Last fallback: calculate from cart items in localStorage
        try {
            const cartData = localStorage.getItem('cart');
            if (cartData) {
                const cart = JSON.parse(cartData);
                if (cart.items && Array.isArray(cart.items)) {
                    return cart.items.reduce((total, item) => {
                        return total + (item.price * item.quantity);
                    }, 0);
                }
            }
        } catch (e) {
            console.warn('ShippingOptionsManager: Could not calculate total from localStorage cart:', e);
        }
        
        return 0;
    }

    /**
     * Uppdaterar endast frakt-visningen utan att påverka totalen
     * Detta förhindrar konflikter med DiscountManager
     */
    updateShippingDisplay() {
        if (!this.selectedOption) return;

        const shippingRow = document.getElementById('order-shipping-row');
        const shippingAmount = document.getElementById('order-shipping-amount');

        if (shippingRow && shippingAmount) {
            // Show shipping row and update amount
            shippingRow.style.display = 'flex';
            
            if (this.selectedOption.price > 0) {
                shippingAmount.textContent = `${this.selectedOption.price.toFixed(2)} kr`;
            } else {
                shippingAmount.textContent = t('shipping.free');
            }
        }
        
        // Trigga en central total-uppdatering istället för att göra det själv
        this.requestTotalUpdate();
    }

    /**
     * Begär en central total-uppdatering från systemet
     */
    requestTotalUpdate() {
        const event = new CustomEvent('requestTotalUpdate', {
            detail: {
                source: 'shipping',
                shippingCost: this.getShippingCost(),
                selectedOption: this.selectedOption
            }
        });
        document.dispatchEvent(event);
    }

    updateOrderTotals() {
        if (!this.selectedOption) return;

        const shippingRow = document.getElementById('order-shipping-row');
        const shippingAmount = document.getElementById('order-shipping-amount');
        const totalAmount = document.getElementById('order-total-amount');
        const subtotalAmount = document.getElementById('order-subtotal-amount');

        if (shippingRow && shippingAmount) {
            // Show shipping row and update amount
            shippingRow.style.display = 'flex';
            
            if (this.selectedOption.price > 0) {
                shippingAmount.textContent = `${this.selectedOption.price.toFixed(2)} kr`;
            } else {
                shippingAmount.textContent = t('shipping.free');
            }
        }

        if (totalAmount && subtotalAmount) {
            // Calculate new total - DiscountManager will handle any shipping discounts
            const subtotal = this.parsePrice(subtotalAmount.textContent);
            const shipping = this.selectedOption.price;
            const total = subtotal + shipping;
            
            totalAmount.textContent = `${total.toFixed(2)} kr`;
        }
    }

    parsePrice(priceText) {
        // Extract numeric value from "123.45 kr" format
        const match = priceText.match(/(\d+(?:\.\d+)?)/);
        return match ? parseFloat(match[1]) : 0;
    }

    showLoading() {
        const container = document.getElementById('shipping-options-list');
        const errorDiv = document.getElementById('shipping-error');
        
        if (container) {
            container.innerHTML = `<div class="shipping-loading">${t('shipping.loading')}</div>`;
        }
        
        if (errorDiv) {
            errorDiv.style.display = 'none';
        }
    }

    showError(message) {
        const container = document.getElementById('shipping-options-list');
        const errorDiv = document.getElementById('shipping-error');
        
        if (container) {
            container.innerHTML = '';
        }
        
        if (errorDiv) {
            errorDiv.querySelector('p').textContent = message;
            errorDiv.style.display = 'block';
        }
    }

    showNoOptions() {
        const container = document.getElementById('shipping-options-list');
        if (container) {
            container.innerHTML = `
                <div class="shipping-no-options">
                    <p>${t('shipping.noOptions')}</p>
                    <p>${t('shipping.contactSupport')}</p>
                </div>
            `;
        }
    }

    hideError() {
        const errorDiv = document.getElementById('shipping-error');
        if (errorDiv) {
            errorDiv.style.display = 'none';
        }
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text || '';
        return div.innerHTML;
    }

    /**
     * Kontrollerar om frakt ska vara gratis baserat på automatiska rabatter
     */
    isShippingFree(option) {
        // Kontrollera om DiscountManager har automatiska frakt-rabatter
        const discountManager = this.system?.modules?.discount || window.ecommerce?.modules?.discount;
        
        if (discountManager && discountManager.automaticDiscounts) {
            // Kolla om det finns automatiska frakt-rabatter som täcker denna frakt
            const shippingDiscounts = discountManager.automaticDiscounts.filter(discount => 
                discount.name && discount.name.toLowerCase().includes('frakt')
            );
            
            if (shippingDiscounts.length > 0) {
                // Om det finns frakt-rabatter, kontrollera om de täcker hela fraktkostnaden
                const totalShippingDiscount = shippingDiscounts.reduce((sum, discount) => 
                    sum + discount.discountAmount, 0
                );
                
                return totalShippingDiscount >= option.price;
            }
        }
        
        return false;
    }

    // Public methods for integration with checkout process
    getSelectedOption() {
        return this.selectedOption;
    }

    getSelectedOptionId() {
        return this.selectedOption ? this.selectedOption.id : null;
    }

    getShippingCost() {
        if (!this.selectedOption) return 0;
        
        // Return the original shipping price - DiscountManager will handle any shipping discounts
        return this.selectedOption.price;
    }

    /**
     * Hämtar vald fraktkostnad för rabatt-beräkningar
     * Alias för getShippingCost() för kompatibilitet med DiscountManager
     */
    getSelectedShippingCost() {
        return this.getShippingCost();
    }

    validateSelection() {
        const selectedRadio = document.querySelector('input[name="shippingOption"]:checked');
        if (!selectedRadio) {
            this.showValidationError(t('shipping.selectRequired'));
            return false;
        }
        return true;
    }

    showValidationError(message) {
        // Add error styling to shipping options section
        const section = document.querySelector('.shipping-options-section');
        if (section) {
            section.classList.add('has-error');
            
            // Remove existing error message
            const existingError = section.querySelector('.validation-error');
            if (existingError) {
                existingError.remove();
            }
            
            // Add new error message
            const errorDiv = document.createElement('div');
            errorDiv.className = 'validation-error';
            errorDiv.textContent = message;
            section.appendChild(errorDiv);
            
            // Remove error after 5 seconds
            setTimeout(() => {
                section.classList.remove('has-error');
                errorDiv.remove();
            }, 5000);
        }
    }

    // Method to refresh shipping options (useful after address changes)
    refresh() {
        if (this.initialized) {
            this.loadShippingOptions();
        }
    }

    // Get shipping data for order submission
    getShippingData() {
        if (!this.selectedOption) return null;
        
        // Use the actual shipping cost (which considers free shipping)
        const actualShippingCost = this.getShippingCost();
        
        return {
            shippingOptionId: this.selectedOption.id,
            shippingOptionName: this.selectedOption.name,
            shippingMethod: this.selectedOption.shippingMethod,
            shippingPrice: actualShippingCost, // Use calculated price, not original price
            originalShippingPrice: this.selectedOption.price, // Keep original for reference
            shippingTaxRate: this.selectedOption.taxRate || 25,
            isFreeShipping: actualShippingCost === 0 && this.selectedOption.price > 0
        };
    }
}
